import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString, uniqBy } from 'lodash';

import { api } from '@/api';
import { API_URL } from '@/dtos';

export interface ServicePayload {
  id: number;
  name: string;
  category_type: string;
}

export interface Service {
  id: string;
  name: string;
  categoryType?: string;
}

interface CoveragePeriod {
  coverage_date_end?: string;
  coverage_date_start?: string;
}

export interface AccountOrderPayload {
  coverage_date_start?: string;
  coverage_date_end?: string;
  services?: ServicePayload[];
  coverage_periods?: CoveragePeriod[];
  is_forfeited?: boolean;
}

interface Props {
  employeeId?: string;
  dependentId?: string;
  serviceDate?: string;
}

const useServicesQuery = ({ employeeId, dependentId, serviceDate }: Props) => {
  const { isLoading, isSuccess, isError, data } = useQuery(
    [API_URL.ACCOUNT_ORDERS_SERVICES, employeeId, dependentId, serviceDate],
    () =>
      // TODO: check this type IAccountOrderMatrixDto
      api.get<AccountOrderPayload[][]>(API_URL.ACCOUNT_ORDERS_SERVICES, {
        params: {
          employee_id: employeeId || undefined,
          dependent_id: dependentId || undefined,
          service_on: serviceDate || undefined,
        },
      }),
    {
      enabled: !!employeeId && !!serviceDate,
      retry: false,
    }
  );

  const formattedServices = useMemo(() => {
    const accountOrders = data?.data?.flat()?.filter((account) => !account?.is_forfeited);

    if (!accountOrders?.length) return [];

    const servicesFromAccountOrders = accountOrders
      ?.flat()
      ?.map((accountOrder) => accountOrder?.services)
      ?.flat();

    const services = servicesFromAccountOrders
      .map((service) => ({
        id: toString(service?.id),
        name: service?.name || '',
        categoryType: service?.category_type || '',
      }))
      .sort((a, b) => (a.name > b.name ? 1 : -1));

    const uniqServices = uniqBy(services, 'name');

    return uniqServices;
  }, [data]);

  return {
    formattedServices: formattedServices as Service[],
    isError,
    isLoading,
    isSuccess,
  };
};

export default useServicesQuery;
